import type { AttributeRule } from 'types';
import { ATTRIBUTE_RULE_OPERATORS } from 'definitions';

/*
 * The same limits PUT /experiments/:id/server-settings enforces
 * (api/experiments/server-settings/validate-server-settings.ts). Mirrored rather than
 * imported because the dashboard must not import server code; the server stays the judge,
 * this only keeps Add disabled until the rule would be accepted.
 */
export const MAX_ATTRIBUTE_RULES = 20;
export const MAX_ATTRIBUTE_NAME_LENGTH = 64;
export const MAX_RULE_LIST_VALUES = 100;

export type AttributeOperator = (typeof ATTRIBUTE_RULE_OPERATORS)[number];

/**
 * How the typed value is read. The evaluator compares strictly, so "42" and 42 are
 * different values: the person says which one they mean instead of it being guessed.
 */
export type RuleValueKind = 'text' | 'number' | 'boolean';

export type RuleDraft = {
  attribute: string;
  operator: AttributeOperator;
  valueKind: RuleValueKind;
  /** One value, or a comma-separated list for 'in' and 'not in'. */
  valueText: string;
};

export type ParsedRule = { ok: true; rule: AttributeRule } | { ok: false; reason: string };

export const EMPTY_RULE_DRAFT: RuleDraft = { attribute: '', operator: 'equals', valueKind: 'text', valueText: '' };

/** The value kinds an operator can take, first one the default. */
export const valueKindsFor = (operator: AttributeOperator): RuleValueKind[] => {
  if (operator === 'exists') return [];
  if (operator === '<' || operator === '>') return ['number'];
  if (operator === 'in' || operator === 'not in') return ['text', 'number'];
  return ['text', 'number', 'boolean'];
};

const toNumber = (text: string): number | null => {
  if (!text.trim()) return null;
  const value = Number(text.trim());
  return Number.isFinite(value) ? value : null;
};

/** Turns what the dialog holds into a rule the API will accept, or says why it would not. */
export function parseRuleDraft(draft: RuleDraft): ParsedRule {
  const attribute = draft.attribute.trim();
  const { operator, valueKind, valueText } = draft;

  if (!attribute) return { ok: false, reason: 'Name the attribute.' };
  if (attribute.length > MAX_ATTRIBUTE_NAME_LENGTH) {
    return { ok: false, reason: `An attribute name is at most ${MAX_ATTRIBUTE_NAME_LENGTH} characters.` };
  }
  if (!(ATTRIBUTE_RULE_OPERATORS as readonly string[]).includes(operator)) {
    return { ok: false, reason: 'Choose an operator.' };
  }

  if (operator === 'exists') return { ok: true, rule: { attribute, operator } };

  if (operator === '<' || operator === '>') {
    const value = toNumber(valueText);
    if (value === null) return { ok: false, reason: 'Enter a number.' };
    return { ok: true, rule: { attribute, operator, value } };
  }

  if (operator === 'in' || operator === 'not in') {
    const parts = valueText
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return { ok: false, reason: 'Enter at least one value, separated by commas.' };
    if (parts.length > MAX_RULE_LIST_VALUES) {
      return { ok: false, reason: `A list holds at most ${MAX_RULE_LIST_VALUES} values.` };
    }
    if (valueKind === 'number') {
      const numbers = parts.map(toNumber);
      if (numbers.some((value) => value === null)) return { ok: false, reason: 'Every value must be a number.' };
      return { ok: true, rule: { attribute, operator, value: numbers as number[] } };
    }
    return { ok: true, rule: { attribute, operator, value: parts } };
  }

  // equals
  if (valueKind === 'boolean') {
    const text = valueText.trim().toLowerCase();
    if (text !== 'true' && text !== 'false') return { ok: false, reason: 'Enter true or false.' };
    return { ok: true, rule: { attribute, operator, value: text === 'true' } };
  }
  if (valueKind === 'number') {
    const value = toNumber(valueText);
    if (value === null) return { ok: false, reason: 'Enter a number.' };
    return { ok: true, rule: { attribute, operator, value } };
  }
  if (!valueText.trim()) return { ok: false, reason: 'Enter a value.' };
  return { ok: true, rule: { attribute, operator, value: valueText.trim() } };
}

/** One line for a rule chip, the value written the way the evaluator compares it. */
export function describeRule(rule: AttributeRule): string {
  if (rule.operator === 'exists') return `${rule.attribute} exists`;
  const value = Array.isArray(rule.value)
    ? `[${rule.value.map((item) => JSON.stringify(item)).join(', ')}]`
    : JSON.stringify(rule.value);
  return `${rule.attribute} ${rule.operator} ${value}`;
}
